// src/screens/s2/DirectorExtraction.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Session 2 Screen 3 — Director Extraction + Retained Earnings.
// Top:    four extraction components (salary / dividends / employer pension /
//         total) plus % of revenue.
// Middle: retained earnings bridge — Opening RE → +Profit → −Dividends →
//         Closing RE, as a 4-step stacked waterfall on ComposedChart.
// Bottom: S1 agreed extraction vs actual Xero extraction, delta shown.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import {
  ComposedChart, Bar, Line, Cell, XAxis, YAxis, Tooltip, CartesianGrid,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { ClientData } from '../../types/ClientData';
import { useXeroData } from '../../hooks/useXeroData';
import { useXeroBalance } from '../../hooks/useXeroBalance';
import { formatCurrency, formatPercent } from '../../utils/formatters';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

function ExtractionCard({
  label, annual, monthly, subtitle, accent,
}: { label: string; annual: number; monthly: number; subtitle: string; accent?: string }) {
  return (
    <div
      className="rounded-2xl p-5"
      style={{
        backgroundColor: accent ? `${accent}14` : '#FFFFFF',
        border: `1px solid ${accent ? `${accent}50` : '#E5E7EB'}`,
      }}
    >
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">{label}</div>
      <div className="text-2xl font-bold text-ma-navy">{formatCurrency(annual)}/yr</div>
      <div className="text-sm text-gray-500 mt-1">{formatCurrency(monthly)}/mo</div>
      <p className="text-xs text-gray-500 mt-2">{subtitle}</p>
    </div>
  );
}

export default function DirectorExtraction({ data }: Props) {
  const xero = useXeroData(data.xeroTenantId);
  const balance = useXeroBalance(data.xeroTenantId);

  const loading = xero.loading || balance.loading;

  if (loading) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="text-sm text-gray-500">Loading extraction data…</div>
      </div>
    );
  }

  const hasXero = xero.mapped && xero.summary;
  const hasBalance = !!balance.data;

  if (!hasXero || !hasBalance) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <h2 className="text-2xl font-bold text-ma-navy mb-4">Director Extraction</h2>
        <div className="rounded-2xl bg-amber-50 border-2 border-amber-300 p-5 text-sm text-amber-800">
          {xero.error || balance.error
            ? <>Xero unavailable. This screen needs live P&amp;L + balance sheet data.</>
            : <>Chart of accounts not yet mapped — open the COA Mapper first.</>}
        </div>
      </div>
    );
  }

  const s = xero.summary!;
  const b = balance.data!;

  // ── Extraction components ───────────────────────────────────────────────
  // S1-agreed figures come from ClientData (adviser captured these in S1).
  const s1Salary = data.salary || 0;
  const s1Dividends = data.dividends || 0;
  const s1Pension = ((data.pensionCompanyFunded || 0) + (data.planPensionFromDividendReduction || 0)) * 12;

  // Live figures from Xero.
  const liveSalary = s.ownerSalary;
  const liveDividends = b.dividendsPaid;     // actual from balance-sheet equity walk
  const livePension = s.ownerPension;

  const liveTotal = liveSalary + liveDividends + livePension;
  const liveExtractionPctOfRevenue = s.revenue > 0 ? (liveTotal / s.revenue) * 100 : 0;

  // ── Retained earnings bridge — stacked-spacer waterfall ───────────────
  // Anchors (Opening RE / Closing RE) sit at floor 0. +Profit floats
  // upward from opening to opening+profit; −Dividends floats downward
  // from opening+profit to closing. Same pattern as BusinessPL waterfall.
  const bridgeData: Array<{ name: string; spacer: number; value: number; running: number; fill: string }> = [];
  let bRun = 0;
  const bAnchor = (name: string, fill: string) => {
    bridgeData.push({ name, spacer: 0, value: bRun, running: bRun, fill });
  };
  const bStep = (name: string, delta: number, fill: string) => {
    const before = bRun;
    const after = before + delta;          // signed delta: + for profit, − for dividends
    bridgeData.push({
      name,
      spacer: Math.min(before, after),
      value: Math.abs(delta),
      running: after,
      fill,
    });
    bRun = after;
  };
  bRun = b.retainedEarningsOpening;
  bAnchor('Opening RE', '#60A5FA');
  bStep('+ Profit', b.profitInPeriod, '#10B981');
  bStep('− Dividends', -b.dividendsPaid, '#DC2626');
  bAnchor('Closing RE', '#C9A84C');

  // ── S1 vs actual comparison ─────────────────────────────────────────────
  const s1Total = s1Salary + s1Dividends + s1Pension;
  const staysInBusiness = s1Total - liveTotal;    // + = client extracting less than S1 plan (good)
  const vsS1Monthly = staysInBusiness / 12;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* HEADER */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-ma-navy">
          Director Extraction
          {xero.tenantName && <span className="ml-2 text-sm font-normal text-gray-500">— {xero.tenantName}</span>}
        </h2>
        <p className="text-gray-500 mt-1 text-sm">
          What's coming out of the business into your hands, and what's
          staying in to build retained earnings.
        </p>
      </div>

      {/* SECTION A — EXTRACTION COMPONENTS */}
      <h3 className="text-sm font-bold text-ma-navy uppercase tracking-wide mb-3">
        Extraction components (live from Xero)
      </h3>
      <div className="grid grid-cols-4 gap-4 mb-6">
        <ExtractionCard
          label="Salary"
          annual={liveSalary}
          monthly={liveSalary / 12}
          subtitle="PAYE salary (S1 agreed)"
        />
        <ExtractionCard
          label="Dividends"
          annual={liveDividends}
          monthly={liveDividends / 12}
          subtitle="Declared in the year"
        />
        <ExtractionCard
          label="Employer Pension"
          annual={livePension}
          monthly={livePension / 12}
          subtitle="CT-deductible contribution"
          accent="#10B981"
        />
        <ExtractionCard
          label="Total extraction"
          annual={liveTotal}
          monthly={liveTotal / 12}
          subtitle={`${formatPercent(liveExtractionPctOfRevenue)} of revenue`}
          accent="#C9A84C"
        />
      </div>

      {/* SECTION B — RETAINED EARNINGS BRIDGE */}
      <h3 className="text-sm font-bold text-ma-navy uppercase tracking-wide mb-3">
        Retained earnings bridge
      </h3>
      <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
        <div style={{ width: '100%', height: 300 }}>
          <ResponsiveContainer>
            <ComposedChart data={bridgeData} margin={{ top: 20, right: 20, bottom: 8, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#9CA3AF" />
              <YAxis tick={{ fontSize: 11 }} stroke="#9CA3AF" tickFormatter={(v) => `£${Math.round(v / 1000)}k`} />
              <Tooltip formatter={(v: any) => formatCurrency(Number(v))} />
              <ReferenceLine y={0} stroke="#9CA3AF" />
              {/* Stacked-spacer waterfall so +Profit and −Dividends bars
                  float at the correct y-range around the opening balance. */}
              <Bar dataKey="spacer" stackId="re" fill="transparent" />
              <Bar dataKey="value" stackId="re" fill="#1E3A5F" radius={[4, 4, 0, 0]}>
                {bridgeData.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} />
                ))}
              </Bar>
              <Line
                type="monotone"
                dataKey="running"
                stroke="#94A3B8"
                strokeWidth={2}
                strokeDasharray="4 2"
                dot={{ r: 3, fill: '#94A3B8' }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <div className="grid grid-cols-4 gap-4 mt-4 pt-4 border-t border-gray-100">
          <div>
            <div className="text-xs text-gray-500">Opening retained earnings</div>
            <div className="text-lg font-bold text-ma-navy">{formatCurrency(b.retainedEarningsOpening)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">+ Profit in period</div>
            <div className="text-lg font-bold text-emerald-700">{formatCurrency(b.profitInPeriod)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">− Dividends paid</div>
            <div className="text-lg font-bold text-red-700">{formatCurrency(b.dividendsPaid)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">= Closing retained earnings</div>
            <div className="text-lg font-bold" style={{ color: '#C9A84C' }}>{formatCurrency(b.retainedEarningsClosing)}</div>
          </div>
        </div>
      </div>

      {/* SECTION C — S1 vs ACTUAL */}
      <h3 className="text-sm font-bold text-ma-navy uppercase tracking-wide mb-3">
        S1 plan vs Xero actual
      </h3>
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
            S1 agreed extraction
          </div>
          <div className="text-2xl font-bold text-ma-navy">{formatCurrency(s1Total)}/yr</div>
          <div className="text-xs text-gray-500 mt-1">{formatCurrency(s1Total / 12)}/mo</div>
          <p className="text-xs text-gray-500 mt-2">
            Salary {formatCurrency(s1Salary)} + dividends {formatCurrency(s1Dividends)} + pension {formatCurrency(s1Pension)}
          </p>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
            Xero actual extraction
          </div>
          <div className="text-2xl font-bold text-ma-navy">{formatCurrency(liveTotal)}/yr</div>
          <div className="text-xs text-gray-500 mt-1">{formatCurrency(liveTotal / 12)}/mo</div>
          <p className="text-xs text-gray-500 mt-2">Last 12 months from P&amp;L + balance sheet</p>
        </div>
        <div
          className="rounded-2xl border-2 p-5"
          style={{
            backgroundColor: staysInBusiness >= 0 ? '#ECFDF5' : '#FEF2F2',
            borderColor: staysInBusiness >= 0 ? '#10B981' : '#DC2626',
          }}
        >
          <div className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: staysInBusiness >= 0 ? '#047857' : '#991B1B' }}>
            {staysInBusiness >= 0 ? 'Staying in the business' : 'Above S1 plan'}
          </div>
          <div className="text-2xl font-bold" style={{ color: staysInBusiness >= 0 ? '#047857' : '#991B1B' }}>
            {formatCurrency(Math.abs(vsS1Monthly))}/mo
          </div>
          <p className="text-xs mt-2" style={{ color: staysInBusiness >= 0 ? '#065F46' : '#7F1D1D' }}>
            {staysInBusiness >= 0
              ? `${formatCurrency(Math.abs(staysInBusiness))} per year below S1 plan — available for reserves or further CT-efficient extraction.`
              : `${formatCurrency(Math.abs(staysInBusiness))} per year more than S1 agreed — review in the capital allocation screen.`}
          </p>
        </div>
      </div>
    </div>
  );
}
